import type { PassFailFieldStatus } from "@adrekic/fieldsjson"
import { LuIcon, Menu } from "@overmap-ai/blocks"
import { EMPTY_ARRAY } from "@overmap-ai/common"
import {
	fieldDataMapping,
	type PassFailField,
	passFailFieldStatuses,
	passFailFieldStatusMapping,
} from "@overmap-ai/forms"
import { use } from "react"

import {
	MultiSelectItemTemplate,
	SelectAllItemTemplate,
	SelectItemTemplate,
} from "../../../components/MenuGroups/constants"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { PassFailFieldFilterDef } from "./PassFailFieldFilterDef"

interface PassFailFieldFilterCell extends FilterDefRenderProps<PassFailFieldFilterDef> {
	field: PassFailField
}

export const PassFailFieldFilterCell = (props: PassFailFieldFilterCell) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton type="button" className="shrink-1 min-w-0 grow">
								<span className="truncate">
									{equalsFilterValue !== undefined
										? equalsFilterValue
											? passFailFieldStatusMapping[equalsFilterValue].label
											: "empty"
										: "..."}
								</span>
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.SelectGroup<PassFailFieldStatus>
								required={false}
								value={equalsFilterValue}
								onValueChange={filter.setFilterValue}
							>
								{passFailFieldStatuses.map((status) => {
									const { label } = passFailFieldStatusMapping[status]
									return (
										<SelectItemTemplate<PassFailFieldStatus> key={status} value={status}>
											<span className="truncate">{label}</span>
										</SelectItemTemplate>
									)
								})}
							</Menu.SelectGroup>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "includes":
			case "excludes": {
				const includesModifier = filterDef.modifiers.includes
				const includesModifierFilterValue =
					filterValue !== undefined && includesModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				const getLabelUi = () => {
					switch ((includesModifierFilterValue ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1: {
							const { icon, label } = passFailFieldStatusMapping[includesModifierFilterValue![0]]

							return (
								<>
									<LuIcon icon={icon} />
									<span className="truncate">{label}</span>
								</>
							)
						}
						default:
							return `${includesModifierFilterValue!.length} statuses`
					}
				}

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton type="button" className="shrink-1 min-w-0 grow">
								<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.MultiSelectGroup<PassFailFieldStatus>
								values={includesModifierFilterValue ?? EMPTY_ARRAY}
								onValuesChange={filter.setFilterValue}
							>
								<SelectAllItemTemplate>Select all</SelectAllItemTemplate>
								{passFailFieldStatuses.map((status) => {
									const { label, icon } = passFailFieldStatusMapping[status]

									return (
										<MultiSelectItemTemplate<PassFailFieldStatus> key={status} value={status}>
											<LuIcon icon={icon} />
											<span className="truncate">{label}</span>
										</MultiSelectItemTemplate>
									)
								})}
							</Menu.MultiSelectGroup>
						</Menu.Content>
					</Menu.Root>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
